
import { FC, useState, useEffect } from 'react';
import { useSupabase } from '../../../hooks/useSupabase';

interface Reservation {
  id: number;
  name: string;
  phone: string;
  date: string;
  time: string;
  guests: number;
  message?: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  created_at: string;
}

interface AdminSettings {
  kakao_phone?: string;
  kakao_template_id?: string;
  kakao_sender_key?: string;
}

const ReservationManagement: FC = () => {
  const { supabase } = useSupabase();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [kakaoSettings, setKakaoSettings] = useState({
    kakao_phone: '',
    kakao_template_id: '',
    kakao_sender_key: ''
  });
  const [showKakaoSettings, setShowKakaoSettings] = useState(false);
  const [savingKakao, setSavingKakao] = useState(false);

  const loadReservations = async () => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('reservations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('예약 로드 실패:', error);
        return;
      }

      setReservations(data || []);
    } catch (error) {
      console.error('예약 로드 중 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadKakaoSettings = async () => {
    if (!supabase) return;

    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('kakao_phone, kakao_template_id, kakao_sender_key')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('카카오톡 설정 로딩 실패:', error);
        return;
      }

      if (data) {
        setKakaoSettings({
          kakao_phone: data.kakao_phone || '',
          kakao_template_id: data.kakao_template_id || '',
          kakao_sender_key: data.kakao_sender_key || ''
        });
      }
    } catch (error) {
      console.error('카카오톡 설정 로딩 중 오류:', error);
    }
  };

  const saveKakaoSettings = async () => {
    if (!supabase) return;

    setSavingKakao(true);
    try {
      const { error } = await supabase
        .from('admin_settings')
        .upsert({
          id: 1,
          kakao_phone: kakaoSettings.kakao_phone,
          kakao_template_id: kakaoSettings.kakao_template_id,
          kakao_sender_key: kakaoSettings.kakao_sender_key
        });

      if (error) throw error;

      alert('카카오톡 알림 설정이 저장되었습니다.');
      setShowKakaoSettings(false);
    } catch (error) {
      console.error('카카오톡 설정 저장 실패:', error);
      alert('설정 저장에 실패했습니다.');
    } finally {
      setSavingKakao(false);
    }
  };

  const updateReservationStatus = async (id: number, status: 'confirmed' | 'cancelled') => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('reservations')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      setReservations(reservations.map(reservation => 
        reservation.id === id ? { ...reservation, status } : reservation
      ));

      alert(`예약이 ${status === 'confirmed' ? '확인' : '취소'}되었습니다.`);
    } catch (error) {
      console.error('예약 상태 업데이트 실패:', error);
      alert('예약 상태 업데이트에 실패했습니다.');
    }
  };

  const deleteReservation = async (id: number) => {
    if (!confirm('정말로 이 예약을 삭제하시겠습니까?')) return;

    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('reservations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setReservations(reservations.filter(reservation => reservation.id !== id));
      alert('예약이 삭제되었습니다.');
    } catch (error) {
      console.error('예약 삭제 실패:', error);
      alert('예약 삭제에 실패했습니다.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed':
        return '확인됨';
      case 'cancelled':
        return '취소됨';
      default:
        return '대기중';
    }
  };

  useEffect(() => {
    loadReservations();
    loadKakaoSettings();
  }, [supabase]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-gray-400 animate-spin mb-4"></i>
          <p className="text-gray-600">예약 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">예약 관리</h2>
        <button
          onClick={() => setShowKakaoSettings(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-message-3-line mr-2"></i>
          카카오톡 알림 설정
        </button>
      </div>

      {/* 카카오톡 설정 모달 */}
      {showKakaoSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">카카오톡 알림 설정</h3>
              <button
                onClick={() => setShowKakaoSettings(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  관리자 전화번호
                </label>
                <input
                  type="tel"
                  value={kakaoSettings.kakao_phone}
                  onChange={(e) => setKakaoSettings(prev => ({ ...prev, kakao_phone: e.target.value }))}
                  placeholder="010-1234-5678"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  카카오톡 템플릿 ID
                </label>
                <input
                  type="text"
                  value={kakaoSettings.kakao_template_id}
                  onChange={(e) => setKakaoSettings(prev => ({ ...prev, kakao_template_id: e.target.value }))}
                  placeholder="템플릿 ID 입력"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  카카오톡 발신키
                </label>
                <input
                  type="text"
                  value={kakaoSettings.kakao_sender_key}
                  onChange={(e) => setKakaoSettings(prev => ({ ...prev, kakao_sender_key: e.target.value }))}
                  placeholder="발신키 입력"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <i className="ri-information-line mr-1"></i>
                  카카오톡 비즈니스 API 설정이 필요합니다. 자세한 설정 방법은 아래 안내를 참고하세요.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowKakaoSettings(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button
                onClick={saveKakaoSettings}
                disabled={savingKakao}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {savingKakao ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 기존 예약 목록 */}
      {reservations.length === 0 ? (
        <div className="text-center py-12">
          <i className="ri-calendar-line text-6xl text-gray-300 mb-4"></i>
          <p className="text-gray-500 text-lg">아직 예약이 없습니다.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reservations.map((reservation) => (
            <div key={reservation.id} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-lg">{reservation.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                      {getStatusText(reservation.status)}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                    <div>
                      <i className="ri-phone-line mr-2"></i>
                      {reservation.phone}
                    </div>
                    <div>
                      <i className="ri-calendar-line mr-2"></i>
                      {reservation.date}
                    </div>
                    <div>
                      <i className="ri-time-line mr-2"></i>
                      {reservation.time}
                    </div>
                    <div>
                      <i className="ri-group-line mr-2"></i>
                      {reservation.guests}명
                    </div>
                  </div>
                  {reservation.message && (
                    <div className="text-sm text-gray-600 mb-3">
                      <i className="ri-message-2-line mr-2"></i>
                      {reservation.message}
                    </div>
                  )}
                  <div className="text-xs text-gray-400">
                    예약일시: {new Date(reservation.created_at).toLocaleString('ko-KR')}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  {reservation.status === 'pending' && (
                    <>
                      <button
                        onClick={() => updateReservationStatus(reservation.id, 'confirmed')}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-check-line mr-1"></i>
                        확인
                      </button>
                      <button
                        onClick={() => updateReservationStatus(reservation.id, 'cancelled')}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-close-line mr-1"></i>
                        취소
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => deleteReservation(reservation.id)}
                    className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    삭제
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReservationManagement;
